/* eslint-disable */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { useTranslation } from "react-i18next";
import { validateOnePageStatus } from "../../services/statusValidationApi";
import { ThreeDots } from "react-loader-spinner";

// Global cache to prevent multiple API calls for the same initiative
const validationCache = new Map();
const pendingValidations = new Map(); // Agora armazena Promises para sharing

const StatusWarningIndicator = ({
  initiativeId,
  currentStatus,
  size = "16px",
  color = "#ff9800",
  showTooltip = true,
  onWarningChange = null,
  onTooltipEnter = null,
  onTooltipLeave = null,
  // NOVAS PROPS PARA LOADING EXTERNO
  externalLoadingState = false,
  onLoadingStateChange = null,
  // NEW: Only validate on demand (hover/click)
  lazyLoad = true,
}) => {
  const { t } = useTranslation();
  const [statusWarning, setStatusWarning] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [hasBeenValidated, setHasBeenValidated] = useState(false);
  const [lastValidationTime, setLastValidationTime] = useState(0);

  // Função para obter cache key
  const getCacheKey = (initiativeId, currentStatus) => `${initiativeId}-${currentStatus}`;

  // Function to validate status
  const performValidation = () => {
    if (!initiativeId || !currentStatus) {
      setStatusWarning(null);
      return;
    }

    const cacheKey = getCacheKey(initiativeId, currentStatus);
    const now = Date.now();
    
    // Check global cache first
    if (validationCache.has(cacheKey)) {
      const cachedResult = validationCache.get(cacheKey);
      const cacheAge = now - (cachedResult.timestamp || 0);
      
      // Cache valid for 5 minutes (300000ms)
      if (cacheAge < 300000) {

        setStatusWarning(cachedResult.data);
        setHasBeenValidated(true);
        if (onWarningChange) {
          onWarningChange(cachedResult.data ? true : false);
        }
        return;
      }
    }

    // Check if already validating this initiative globally
    if (pendingValidations.has(initiativeId)) {
      // ✅ AGUARDAR Promise existente ao invés de simplesmente retornar
      const existingPromise = pendingValidations.get(initiativeId);
      existingPromise.then((result) => {
        setStatusWarning(result);
        setHasBeenValidated(true);
        if (onWarningChange) {
          onWarningChange(!!result);
        }
      }).catch((error) => {
        console.error("Error in shared validation:", error);
        setStatusWarning(null);
      });
      return;
    }

    // Prevent concurrent calls
    if (!isValidating && (now - lastValidationTime) > 1000) { // Debounce 1 second
      const validateStatus = async () => {
        try {
          setIsValidating(true);
          
                      // ✅ CRIAR Promise que será compartilhada
            const validationPromise = (async () => {
              // NOTIFICAR COMPONENTE PAI SOBRE ESTADO DE LOADING
              if (onLoadingStateChange) {
                onLoadingStateChange(true);
              }
              setLastValidationTime(now);
            
            const response = await validateOnePageStatus(
              initiativeId,
              null,
              currentStatus,
            );

            const result = response.hasWarning ? {
              warningMessage: response.warningMessage,
              warningDetails: response.warningDetails,
              calculatedStatus: response.calculatedStatus,
            } : null;

            // Save to global cache with timestamp
            validationCache.set(cacheKey, {
              data: result,
              timestamp: now
            });
            
            return result;
          })();
          
          // ✅ ARMAZENAR Promise para compartilhamento
          pendingValidations.set(initiativeId, validationPromise);
          
          // ✅ AGUARDAR resultado desta instância
          const result = await validationPromise;
          
          setStatusWarning(result);
          setHasBeenValidated(true);

          // Notificar componente pai sobre mudança no warning
          if (onWarningChange) {
            onWarningChange(!!result);
          }
        } catch (error) {
          console.error("Error validating status:", error);
          setStatusWarning(null);
        } finally {
          setIsValidating(false);
          // ✅ REMOVER Promise do pending
          pendingValidations.delete(initiativeId);
          // NOTIFICAR COMPONENTE PAI SOBRE FIM DO LOADING
          if (onLoadingStateChange) {
            onLoadingStateChange(false);
          }
        }
      };

      validateStatus();
    }
  };

  // Only validate immediately if not lazy loading
  useEffect(() => {
    if (!lazyLoad) {
      performValidation();
    }
  }, [initiativeId, currentStatus, lazyLoad]);

  // Handle hover/click to trigger validation for lazy loading
  const handleInteraction = () => {
    if (lazyLoad && !hasBeenValidated) {
      performValidation();
    }
  };

  // Se não há warning, não renderizar nada
  // CORRIGIDO: Não mostrar durante validação para evitar "flash" de warnings
  if (!statusWarning) {
    return null;
  }

  // SE ESTIVER EM LOADING EXTERNO, MOSTRAR THREEDOTS
  if (externalLoadingState) {
    return (
      <div
        style={{
          width: size,
          height: size,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <ThreeDots
          color={color}
          height={parseInt(size) * 0.6}
          width={parseInt(size) * 0.6}
          ariaLabel="loading warning"
        />
      </div>
    );
  }

  // Construir mensagem de warning baseada no status calculado
  const getWarningMessage = () => {
    if (!statusWarning || !statusWarning.warningDetails) return "";

    const { warningDetails } = statusWarning;
    const statusLabel = t(
      `initiative_selected.${statusWarning.calculatedStatus}`,
    );

    // Determina o tipo de warning baseado nos detalhes
    const hasRedDeliverables =
      warningDetails.red_deliverables &&
      warningDetails.red_deliverables.length > 0;
    const hasYellowDeliverables =
      warningDetails.yellow_deliverables &&
      warningDetails.yellow_deliverables.length > 0;
    const hasRedRisks =
      warningDetails.red_risks && warningDetails.red_risks.length > 0;
    const hasYellowRisks =
      warningDetails.yellow_risks && warningDetails.yellow_risks.length > 0;

    // Determina a mensagem baseada no status calculado (prioridade: vermelho > amarelo > verde)
    if (statusWarning.calculatedStatus === "red") {
      // Para status vermelho, prioriza riscos vermelhos sobre entregáveis vermelhos
      if (hasRedRisks) {
        return `${t("status_validation.status_should_be")} ${statusLabel} ${t(
          "status_validation.due_to_red_high_risks",
        )}`;
      } else if (hasRedDeliverables) {
        return `${t("status_validation.status_should_be")} ${statusLabel} ${t(
          "status_validation.due_to_red_deliverables",
        )}`;
      } else {
        // Caso combinado (entregáveis amarelos + riscos vermelhos) - sempre mostra riscos vermelhos
        return `${t("status_validation.status_should_be")} ${statusLabel} ${t(
          "status_validation.due_to_red_high_risks",
        )}`;
      }
    } else if (statusWarning.calculatedStatus === "yellow") {
      // Para status amarelo, prioriza entregáveis amarelos sobre riscos amarelos
      if (hasYellowDeliverables) {
        return `${t("status_validation.status_should_be")} ${statusLabel} ${t(
          "status_validation.due_to_yellow_deliverables",
        )}`;
      } else if (hasYellowRisks) {
        return `${t("status_validation.status_should_be")} ${statusLabel} ${t(
          "status_validation.due_to_yellow_high_risks",
        )}`;
      } else {
        return `${t("status_validation.status_should_be")} ${statusLabel} ${t(
          "status_validation.based_on_initiative_rules",
        )}`;
      }
    } else {
      return `${t("status_validation.status_should_be")} ${statusLabel} ${t(
        "status_validation.based_on_initiative_rules",
      )}`;
    }
  };

  // Componente para círculo de status colorido
  const StatusCircle = ({ status, size = "12px" }) => {
    const getStatusColor = (status) => {
      switch (status) {
        case "red":
          return "#E04F5F";
        case "yellow":
          return "#F4C430";
        case "green":
          return "#4CAF50";
        default:
          return "#9E9E9E";
      }
    };

    return (
      <span
        style={{
          display: "inline-block",
          width: size,
          height: size,
          borderRadius: "50%",
          backgroundColor: getStatusColor(status),
          marginRight: "6px",
          verticalAlign: "middle",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
        }}
      />
    );
  };

  // Construir conteúdo do tooltip
  const getTooltipContent = () => {
    if (!statusWarning) return "";

    const reportedLabel = t(`initiative_selected.${currentStatus}`);
    const calculatedLabel = t(
      `initiative_selected.${statusWarning.calculatedStatus}`,
    );
    const reason = getWarningMessage();

    return (
      <span>
        <div style={{ fontWeight: "bold", marginBottom: "8px" }}>
          {t("status_validation.warning_title") || "Aviso de Status"}
        </div>

        <div style={{ marginBottom: "8px" }}>
          <span style={{ fontWeight: "bold" }}>
            {t("status_validation.reported_status")}:
          </span>{" "}
          <StatusCircle status={currentStatus} size="14px" />
          {reportedLabel}
        </div>

        <div style={{ marginBottom: "8px" }}>
          <span style={{ fontWeight: "bold" }}>
            {t("status_validation.calculated_status")}:
          </span>{" "}
          <StatusCircle status={statusWarning.calculatedStatus} size="14px" />
          {calculatedLabel}
        </div>

        <div style={{ marginBottom: "12px" }}>
          <span style={{ fontWeight: "bold" }}>
            {t("status_validation.reason")}:
          </span>{" "}
          {reason}
        </div>

        {/* Detalhes específicos */}
        {statusWarning.warningDetails?.red_deliverables?.length > 0 && (
          <div style={{ marginBottom: "8px" }}>
            <span style={{ fontWeight: "bold" }}>
              <StatusCircle status="red" size="12px" />
              {t("status_validation.red_deliverables")}:
            </span>
            {statusWarning.warningDetails.red_deliverables.map((del, index) => (
              <div key={index} style={{ marginLeft: "12px", fontSize: "12px" }}>
                • {del.name}
              </div>
            ))}
          </div>
        )}

        {statusWarning.warningDetails?.yellow_deliverables?.length > 0 && (
          <div style={{ marginBottom: "8px" }}>
            <span style={{ fontWeight: "bold" }}>
              <StatusCircle status="yellow" size="12px" />
              {t("status_validation.yellow_deliverables")}:
            </span>
            {statusWarning.warningDetails.yellow_deliverables.map((del, index) => (
              <div
                key={index}
                style={{ marginLeft: "12px", fontSize: "12px" }}
              >
                • {del.name}
              </div>
            ))}
          </div>
        )}

        {statusWarning.warningDetails?.red_risks?.length > 0 && (
          <div style={{ marginBottom: "8px" }}>
            <span style={{ fontWeight: "bold" }}>
              <StatusCircle status="red" size="12px" />
              {t("status_validation.red_high_risks")}:
            </span>
            {statusWarning.warningDetails.red_risks.map((risk, index) => (
              <div key={index} style={{ marginLeft: "12px", fontSize: "12px" }}>
                • {risk.name} ({t("status_validation.probability")}:{" "}
                {risk.probability}, {t("status_validation.impact")}:{" "}
                {risk.impact})
              </div>
            ))}
          </div>
        )}

        {statusWarning.warningDetails?.yellow_risks?.length > 0 && (
          <div style={{ marginBottom: "8px" }}>
            <span style={{ fontWeight: "bold" }}>
              <StatusCircle status="yellow" size="12px" />
              {t("status_validation.yellow_high_risks")}:
            </span>
            {statusWarning.warningDetails.yellow_risks.map((risk, index) => (
              <div key={index} style={{ marginLeft: "12px", fontSize: "12px" }}>
                • {risk.name} ({t("status_validation.probability")}:{" "}
                {risk.probability}, {t("status_validation.impact")}:{" "}
                {risk.impact})
              </div>
            ))}
          </div>
        )}
      </span>
    );
  };

  const warningIcon = (
    <FontAwesomeIcon
      icon={faExclamationTriangle}
      style={{
        fontSize: size,
        color: color,
        filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))",
      }}
    />
  );

  // Se não deve mostrar tooltip, retornar apenas o ícone
  if (!showTooltip) {
    return warningIcon;
  }

  // Retornar com tooltip
  return (
    <Tippy
      content={getTooltipContent()}
      placement="top"
      arrow={true}
      theme="light"
      interactive={true}
      maxWidth={400}
      zIndex={9999999999}
      appendTo={() => document.body}
      popperOptions={{
        modifiers: [
          {
            name: 'zIndex',
            enabled: true,
            phase: 'write',
            fn: ({ state }) => {
              state.styles.popper.zIndex = '9999999999';
            }
          }
        ]
      }}
    >
      <span 
        style={{ cursor: "pointer" }}
        onMouseEnter={() => {
          if (onTooltipEnter) {
            onTooltipEnter();
          }
        }}
        onMouseLeave={() => {
          if (onTooltipLeave) {
            onTooltipLeave();
          }
        }}
      >
        {warningIcon}
      </span>
    </Tippy>
  );
};

StatusWarningIndicator.propTypes = {
  initiativeId: PropTypes.string.isRequired,
  currentStatus: PropTypes.string.isRequired,
  size: PropTypes.string,
  color: PropTypes.string,
  showTooltip: PropTypes.bool,
  onWarningChange: PropTypes.func,
  onTooltipEnter: PropTypes.func,
  onTooltipLeave: PropTypes.func,
  // NOVAS PROPS PARA LOADING EXTERNO
  externalLoadingState: PropTypes.bool,
  onLoadingStateChange: PropTypes.func,
};

StatusWarningIndicator.defaultProps = {
  size: "16px",
  color: "#ff9800",
  showTooltip: true,
  onWarningChange: null,
  onTooltipEnter: null,
  onTooltipLeave: null,
  // NOVAS PROPS PARA LOADING EXTERNO
  externalLoadingState: false,
  onLoadingStateChange: null,
};

export default StatusWarningIndicator;
